import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSearchParams, Link } from "react-router";
import debounce from "lodash.debounce";
import Select from "react-select";
import {
    fetchBenefitNetworksBySupplierNetwork,
    fetchHrpProviderNetworkList,
    fetchSupplierNetworksByBenefitNetwork,
} from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";

const limit = 20;

const typeOptions = [
    { value: "supplier", label: "Supplier networks" },
    { value: "benefit", label: "Benefit networks" },
    { value: "both", label: "Both" },
];

const statusOptions = [
    { value: "a", label: "Active (a)" },
];

const typeFromParam = (value) => typeOptions.find((o) => o.value === value) || typeOptions[0];

const withQuery = (path, params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return s ? `${path}?${s}` : path;
};

const flattenRelated = (row, networkType) => ({
    networkType,
    typeLabel: networkType === "benefit" ? "Benefit" : "Supplier",
    networkName: row.name,
    networkId: row.id,
    networkKey: row.key,
    status: row.status,
    networkDesc: row.weight != null ? `weight ${row.weight}` : "",
});

const ProviderNetworks = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const benefitNetworkId = searchParams.get("benefitNetworkId") || "";
    const supplierNetworkHccId = searchParams.get("supplierNetworkHccId") || "";
    const networkName = searchParams.get("networkName") || "";
    const relatedFilter = benefitNetworkId || supplierNetworkHccId;

    const [searchValue, setSearchValue] = useState(searchParams.get("name") || "");
    const [decouncedSearchValue, setDecouncedSearchValue] = useState(searchParams.get("name") || "");
    const [typeFilter, setTypeFilter] = useState(() => typeFromParam(searchParams.get("type")));
    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);
    const { ref, inView } = useInView();

    useEffect(() => {
        setTypeFilter(typeFromParam(searchParams.get("type")));
    }, [searchParams]);

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: [
            "providerNetworks",
            limit,
            decouncedSearchValue,
            typeFilter?.value,
            statusFilter?.value,
            benefitNetworkId,
            supplierNetworkHccId,
        ],
        queryFn: async ({ pageParam }) => {
            const page = typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;
            const pageSize = limit;

            if (benefitNetworkId) {
                const result = await fetchSupplierNetworksByBenefitNetwork({
                    benefitNetworkId,
                    page,
                    pageSize,
                });
                return {
                    count: Number(result?.meta?.total) || 0,
                    rows: (result?.data || []).map((row) => flattenRelated(row, "supplier")),
                };
            }
            if (supplierNetworkHccId) {
                const result = await fetchBenefitNetworksBySupplierNetwork({
                    supplierNetworkHccId,
                    page,
                    pageSize,
                });
                return {
                    count: Number(result?.meta?.total) || 0,
                    rows: (result?.data || []).map((row) => flattenRelated(row, "benefit")),
                };
            }

            const params = {
                page,
                pageSize,
                status: statusFilter?.value || "a",
            };
            if (typeFilter?.value && typeFilter.value !== "both") {
                params.type = typeFilter.value;
            }
            if (decouncedSearchValue) {
                params.name = decouncedSearchValue;
            }
            const result = await fetchHrpProviderNetworkList(params);
            return {
                count: Number(result?.meta?.total) || 0,
                rows: (result?.data || []).map((row) => ({
                    ...row,
                    typeLabel: row.networkType === "benefit" ? "Benefit" : "Supplier",
                })),
            };
        },
        select: (data) => {
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage?.rows?.length || lastPage.rows.length < limit) return undefined;
            const loaded = pages.reduce((accum, page) => accum + (page.rows?.length || 0), 0);
            const total = Number(lastPage.count) || 0;
            if (total && loaded >= total) return undefined;
            return pages.length + 1;
        },
        staleTime: 0,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const columns = ["typeLabel", "networkName", "networkId", "status", "networkDesc", "contracts"];

    const toContracts = (row) => {
        if (row.networkType === "benefit") return null;
        const id = row.networkId || row.networkKey;
        return id
            ? withQuery("/providers/contracts", {
                supplierNetworkHccId: id,
                networkName: row.networkName,
            })
            : null;
    };

    const columnLinks = {
        contracts: toContracts,
    };

    const relatedLabel = benefitNetworkId
        ? `Supplier networks in benefit network ${networkName || benefitNetworkId}`
        : supplierNetworkHccId
            ? `Benefit networks for supplier network ${networkName || supplierNetworkHccId}`
            : null;

    const clearFilters = () => {
        setDecouncedSearchValue("");
        setSearchValue("");
        setTypeFilter(typeOptions[0]);
        setStatusFilter(statusOptions[0]);
        if (relatedFilter) setSearchParams({});
    };

    return (
        <div className={"container "}>
            <p className="title">Networks  ( {data?.count || 0} )</p>
            <p className="sub-title">
                Supplier networks group contracting organizations. Benefit networks set in-network plan tiers.
                Open Detail for links to the suppliers, roles, contracts, and related networks.{" "}
                <Link to="/providers/guide">How this wires up in HRP</Link>
            </p>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                    {relatedFilter && (
                        <Select
                            name="relatedNetwork"
                            className="react-select-container large-input"
                            classNamePrefix="react-select"
                            placeholder="Network"
                            isClearable
                            value={{
                                value: relatedFilter,
                                label: relatedLabel,
                            }}
                            options={[{ value: relatedFilter, label: relatedLabel }]}
                            onChange={(option) => {
                                if (!option) setSearchParams({});
                            }}
                        />
                    )}
                    {!relatedFilter && (
                        <>
                            <Select
                                name={"type"}
                                className="react-select-container large-input"
                                classNamePrefix="react-select"
                                placeholder={"Network type"}
                                options={typeOptions}
                                value={typeFilter}
                                onChange={(option) => { setTypeFilter(option); }}
                            />
                            <input
                                style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                                type="text"
                                id={"search"}
                                placeholder="network name..."
                                autoComplete="off"
                                onChange={(e) => {
                                    setSearchValue(e.target.value);
                                    debouncedChangeHandler(e);
                                }}
                                value={searchValue}
                            />
                            <Select
                                name={"status"}
                                className="react-select-container large-input"
                                classNamePrefix="react-select"
                                placeholder={"Status"}
                                isClearable
                                options={statusOptions}
                                value={statusFilter}
                                onChange={(option) => { setStatusFilter(option); }}
                            />
                        </>
                    )}
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={clearFilters}
                >
                    Clear filters
                </p>
            </div>

            {status === "loading" || status === "pending" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"provider-networks"}
                    columns={columns}
                    items={(data?.rows || []).map((row) => ({
                        ...row,
                        contracts: row.networkType === "supplier" ? "Contracts" : null,
                    }))}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    searchText={decouncedSearchValue}
                    columnLinks={columnLinks}
                    ref={ref}
                />
            )}
        </div>
    );
};

export default ProviderNetworks;
